import React, { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'

import {
    Box,
    Button,
    Checkbox,
    CircularProgress,
    FormControlLabel,
    TextField,
    Typography,
} from '@material-ui/core'
import { Alert } from '@material-ui/lab'
import { makeStyles, Theme } from '@material-ui/core/styles'

import { useScreeningMock } from '../../hooks/Outreach/useScreeningMock'

const useStyles = makeStyles((theme: Theme) => ({
    page: {
        minHeight: '100vh',
        background:
            'linear-gradient(160deg, #f0f4f8 0%, #e8eef5 45%, #f7f3ee 100%)',
        padding: theme.spacing(4, 2),
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    card: {
        maxWidth: 520,
        width: '100%',
        backgroundColor: '#fff',
        borderRadius: 12,
        boxShadow: '0 8px 24px rgba(15, 23, 42, 0.08)',
        padding: theme.spacing(4),
    },
    property: {
        fontSize: '1.75rem',
        fontWeight: 700,
        letterSpacing: '-0.02em',
        color: '#0f172a',
        marginBottom: theme.spacing(0.5),
    },
    subtitle: {
        color: '#64748b',
        marginBottom: theme.spacing(2),
    },
    banner: {
        marginBottom: theme.spacing(3),
    },
    field: {
        marginBottom: theme.spacing(2),
    },
    actions: {
        marginTop: theme.spacing(2),
    },
    done: {
        textAlign: 'center',
        padding: theme.spacing(2, 0),
    },
    opening: {
        textAlign: 'center',
    },
    openingText: {
        marginTop: theme.spacing(2),
    },
}))

export const ScreeningMockPage: React.FC = () => {
    const classes = useStyles()
    const { token } = useParams<{ token: string }>()
    const {
        state,
        isLoading,
        error,
        submit,
        isSubmitting,
        submitError,
    } = useScreeningMock(token)

    const [ssnLast4, setSsnLast4] = useState('')
    const [dob, setDob] = useState('')
    const [monthlyIncome, setMonthlyIncome] = useState('')
    const [consent, setConsent] = useState(false)
    const [validationError, setValidationError] = useState('')
    const redirectUrl = state?.redirect_url || ''

    useEffect(() => {
        if (redirectUrl) {
            window.location.replace(redirectUrl)
        }
    }, [redirectUrl])

    const handleSubmit = async (event: React.FormEvent) => {
        event.preventDefault()
        setValidationError('')
        if (!/^\d{4}$/.test(ssnLast4.trim())) {
            setValidationError('Enter the last 4 digits of SSN.')
            return
        }
        if (!dob.trim()) {
            setValidationError('Enter date of birth.')
            return
        }
        if (!monthlyIncome.trim()) {
            setValidationError('Enter monthly income.')
            return
        }
        if (!consent) {
            setValidationError('Consent is required to continue.')
            return
        }
        try {
            await submit({
                ssn_last4: ssnLast4.trim(),
                date_of_birth: dob.trim(),
                monthly_income: monthlyIncome.trim(),
                consent: true,
            })
        } catch {
            // Error surfaced via submitError
        }
    }

    if (isLoading) {
        return (
            <Box className={classes.page}>
                <CircularProgress />
            </Box>
        )
    }

    if (error || !state) {
        return (
            <Box className={classes.page}>
                <Box className={classes.card}>
                    <Alert severity="error">
                        {error?.message ||
                            'This screening link is invalid or expired.'}
                    </Alert>
                </Box>
            </Box>
        )
    }

    if (redirectUrl) {
        return (
            <Box className={classes.page}>
                <Box className={`${classes.card} ${classes.opening}`}>
                    <CircularProgress />
                    <Typography
                        className={`${classes.subtitle} ${classes.openingText}`}
                    >
                        Opening background screening…
                    </Typography>
                </Box>
            </Box>
        )
    }

    const showForm = state.can_submit
    const showDone =
        state.submitted ||
        state.status === 'PROCESSING' ||
        state.status === 'COMPLETE'

    return (
        <Box className={classes.page}>
            <Box className={classes.card}>
                <Typography className={classes.property}>
                    {state.property_name}
                </Typography>
                <Typography className={classes.subtitle}>
                    Screening for {state.prospect_name}
                </Typography>

                <Alert severity="warning" className={classes.banner}>
                    {state.message}
                </Alert>

                {showDone && !showForm ? (
                    <Box className={classes.done}>
                        <Typography variant="h6" gutterBottom>
                            Screening in progress
                        </Typography>
                        <Typography color="textSecondary">
                            Thanks — your information was submitted. The
                            property team will see this screening as Processing
                            until a report result is available.
                        </Typography>
                    </Box>
                ) : (
                    <form onSubmit={handleSubmit}>
                        <TextField
                            className={classes.field}
                            label="SSN (last 4)"
                            value={ssnLast4}
                            onChange={(e) => setSsnLast4(e.target.value)}
                            fullWidth
                            variant="outlined"
                            inputProps={{ maxLength: 4, inputMode: 'numeric' }}
                        />
                        <TextField
                            className={classes.field}
                            label="Date of birth"
                            type="date"
                            value={dob}
                            onChange={(e) => setDob(e.target.value)}
                            fullWidth
                            variant="outlined"
                            InputLabelProps={{ shrink: true }}
                        />
                        <TextField
                            className={classes.field}
                            label="Monthly income ($)"
                            value={monthlyIncome}
                            onChange={(e) => setMonthlyIncome(e.target.value)}
                            fullWidth
                            variant="outlined"
                        />
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={consent}
                                    onChange={(e) =>
                                        setConsent(e.target.checked)
                                    }
                                    color="primary"
                                />
                            }
                            label="I authorize this mock background / credit check for demo purposes."
                        />

                        {(validationError || submitError) && (
                            <Alert severity="error" className={classes.banner}>
                                {validationError || submitError}
                            </Alert>
                        )}

                        <Box className={classes.actions}>
                            <Button
                                type="submit"
                                color="primary"
                                variant="contained"
                                disabled={isSubmitting}
                                fullWidth
                            >
                                {isSubmitting ? 'Submitting…' : 'Submit'}
                            </Button>
                        </Box>
                    </form>
                )}
            </Box>
        </Box>
    )
}
